import { useCallback, useMemo } from 'react';

import { useQuery } from '@tanstack/react-query';

import type { SeriesRecord } from '@/components/Chart/types/chartData';
import { findSeriesBasedOnKey } from '@/components/Chart/utils/findSeriesBasedOnKey';
import { getShortTitle } from '@/components/Chart/utils/getShortTitle';
import { parseDataToChartSeries } from '@/components/Chart/utils/parseDataToChartSeries';
import { Permission } from '@/entities/permissions';
import { chartOptions } from '@/features/Charts/api';
import usePeriodAbsFormatter from '@/hooks/usePeriodAbsFormatter';
import { generateMockSeries } from '@/mocks/chartSeries.mock';
import usePermission from '@/shared/hooks/usePermission';
import type { TemplateChartSeries } from '@/types/charts';

import type { TimeSeriesPoint, TimeSeriesPointCustom } from '../utils/buildChartLineSeries';

const useChartSeries = (
  templateChartSeries: TemplateChartSeries[] | undefined,
  isDhChart = false,
  isDhChartFullscreen = false,
  isInViewport = true
) => {
  const { formatPeriodAbs } = usePeriodAbsFormatter();
  const {
    data: chartData,
    isError: isChartDataError,
    isLoading: isChartDataLoading
  } = useQuery({
    ...chartOptions.data('spot', templateChartSeries),
    enabled: templateChartSeries !== undefined && isInViewport
  });

  const takeSpotData = useCallback(
    (tsData: SeriesRecord, decimalPlaces: number): TimeSeriesPoint => {
      const x = tsData[0];
      const y = Number(tsData[1]);
      const custom: TimeSeriesPointCustom = {
        periodAbs: tsData.length > 2 ? formatPeriodAbs(tsData[2]) : '',
        decimalPlaces
      };
      return { x, y, custom };
    },
    [formatPeriodAbs]
  );
  const { hasPermission } = usePermission();

  const chartSeries = useMemo(() => {
    if (isDhChart && !hasPermission(Permission.UseSmallChartWatermark)) {
      return [generateMockSeries()];
    }
    if (chartData) {
      const dataSymbols = chartData.symbols;
      return dataSymbols.flatMap(({ key, metadata, series, transactionDate }, symbolIndex) => {
        const templateSeries = findSeriesBasedOnKey(templateChartSeries, key);
        const shortTitle = getShortTitle(templateSeries);
        const parseableElements = {
          color: templateSeries?.settings.color,
          symbolIndex,
          shortTitle,
          key,
          metadata,
          transactionDate
        };
        return parseDataToChartSeries(series, parseableElements, isDhChartFullscreen, takeSpotData);
      });
    }
  }, [isDhChart, hasPermission, chartData, templateChartSeries, isDhChartFullscreen, takeSpotData]);

  return {
    loading: isChartDataLoading || !templateChartSeries || (!isInViewport && !chartData),
    error: isChartDataError,
    chartSeries
  };
};

export default useChartSeries;
